import { useMemo, useRef, useEffect, useState } from 'react';
import moment from 'moment';
import { IClient } from '../contexts/ClientsContext';

interface PlanVerificationResult {
  hasPlan: boolean;
  temAvisos: boolean;
  temIntegracaoBolsa: boolean;
  temIntegracaoExterior: boolean;
}

export const usePlanVerification = (selectedClient: IClient | null): PlanVerificationResult => {
  const [cachedResult, setCachedResult] = useState<PlanVerificationResult | null>(null);
  const previousClientRef = useRef<IClient | null>(null);
  
  // Memoizar verificação de plano ativo com cache inteligente
  const hasPlan = useMemo(() => {
    if (!selectedClient) return false;
    
    // Se o cliente não mudou e temos cache, retornar valor em cache
    if (previousClientRef.current === selectedClient && cachedResult) {
      return cachedResult.hasPlan;
    }
    
    // Converter planDueDate para Date se for number (timestamp)
    const planDueDate = typeof selectedClient.planDueDate === 'number' 
      ? new Date(selectedClient.planDueDate)
      : selectedClient.planDueDate;

    const result = (
      selectedClient.planActive ||
      moment(planDueDate || 2021).isAfter(
        moment(new Date())
      ) ||
      selectedClient.planType === "exterior"
    );
    
    return result;
  }, [selectedClient?.planActive, selectedClient?.planDueDate, selectedClient?.planType, selectedClient, cachedResult]);

  // Memoizar verificação de integração com bolsa
  const temIntegracaoBolsa = useMemo(() => 
    selectedClient?.statusBolsa === "Integração ativa", 
    [selectedClient?.statusBolsa]
  );
  
  // Memoizar verificação de integração com exterior
  const temIntegracaoExterior = useMemo(() =>
    selectedClient?.statusExterior === "Integração ativa",
    [selectedClient?.statusExterior]
  );

  // Memoizar verificação de avisos
  const temAvisos = useMemo(() => {
    if (!selectedClient) return false;
    
    return (
      !hasPlan ||
      !temIntegracaoBolsa ||
      !temIntegracaoExterior
    );
  }, [hasPlan, temIntegracaoBolsa, temIntegracaoExterior]);

  // Atualizar cache quando o cliente muda
  useEffect(() => {
    if (selectedClient && selectedClient !== previousClientRef.current) {
      const newResult = {
        hasPlan,
        temAvisos,
        temIntegracaoBolsa,
        temIntegracaoExterior
      };
      setCachedResult(newResult);
      previousClientRef.current = selectedClient;
    }
  }, [selectedClient, hasPlan, temAvisos, temIntegracaoBolsa, temIntegracaoExterior]);

  // Retornar resultado em cache se disponível, senão calcular
  if (cachedResult && previousClientRef.current === selectedClient) {
    return cachedResult;
  }

  return {
    hasPlan,
    temAvisos,
    temIntegracaoBolsa,
    temIntegracaoExterior
  };
};
